import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * SegmentedControl — a compact single-select for a value or filter (Day/Week/
 * Month, list/map, $/€), styled as a track with a dark active segment. It's a
 * styled radio group, NOT navigation: use `Tabs` to switch content panels, and
 * `ButtonGroup` to join related *actions*.
 *
 *   <SegmentedControl
 *     aria-label="Date range"
 *     options={[{ value: 'day', label: 'Day' }, …]}
 *     value={range}
 *     onValueChange={setRange}
 *   />
 *
 * Tokens (Figma → repo): gray-100 track; the selected segment is a `gray-900`
 * fill with white text — a deliberate *neutral* "chosen" signal, distinct from
 * the brand-green that means "primary action". Inactive segments are gray-600 →
 * gray-900 on hover. `size` matches the Button scale (`xs–xl`) and the outer
 * height lines up with a Button of the same size. Full keyboard support
 * (arrows / Home / End) and `role="radiogroup"`.
 */

type SegmentedSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface SegmentedControlOption {
  value: string
  label?: React.ReactNode
  /** Leading icon (or the whole segment, for an icon-only control). */
  icon?: React.ReactNode
  disabled?: boolean
  /** Accessible name — required when the segment is icon-only. */
  'aria-label'?: string
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[]
  /** Selected value (controlled). */
  value?: string
  /** Initial value (uncontrolled). */
  defaultValue?: string
  onValueChange?: (value: string) => void
  size?: SegmentedSize
  /** Disable the whole control. */
  disabled?: boolean
  /** Stretch segments to fill the width (equal widths). */
  fullWidth?: boolean
  /** Accessible name for the group. */
  'aria-label'?: string
  className?: string
}

/** Per-size track padding/radius + segment height/padding/text/glyph. */
const sizeStyles: Record<SegmentedSize, { root: string; segment: string }> = {
  xs: {
    root: 'p-0.5 rounded-md',
    segment: 'h-5 gap-1 rounded px-2 text-xs [&>svg]:size-3.5',
  },
  sm: {
    root: 'p-0.5 rounded-lg',
    segment: 'h-7 gap-1.5 rounded-md px-2.5 text-xs [&>svg]:size-4',
  },
  md: {
    root: 'p-1 rounded-lg',
    segment: 'h-7 gap-1.5 rounded-md px-3 text-sm [&>svg]:size-4',
  },
  lg: {
    root: 'p-1 rounded-xl',
    segment: 'h-8 gap-2 rounded-lg px-3.5 text-base [&>svg]:size-5',
  },
  xl: {
    root: 'p-1 rounded-xl',
    segment: 'h-10 gap-2 rounded-lg px-4 text-base [&>svg]:size-6',
  },
}

export const SegmentedControl = React.forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>(
  (
    {
      options,
      value,
      defaultValue,
      onValueChange,
      size = 'md',
      disabled = false,
      fullWidth = false,
      className,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState<string | undefined>(
      defaultValue
    )
    const selected = isControlled ? value : internal
    const segmentRefs = React.useRef<(HTMLButtonElement | null)[]>([])

    const firstEnabled = options.findIndex((o) => !o.disabled)
    const selectedIndex = options.findIndex((o) => o.value === selected)
    // The single tab-stop: the selected segment, else the first enabled one.
    const focusIndex = selectedIndex >= 0 ? selectedIndex : firstEnabled

    const select = (index: number) => {
      const opt = options[index]
      if (!opt || opt.disabled || disabled) return
      if (!isControlled) setInternal(opt.value)
      onValueChange?.(opt.value)
      segmentRefs.current[index]?.focus()
    }

    const move = (dir: 1 | -1) => {
      const n = options.length
      if (!n) return
      let i = focusIndex < 0 ? 0 : focusIndex
      for (let step = 0; step < n; step++) {
        i = (i + dir + n) % n
        if (!options[i].disabled) break
      }
      select(i)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          move(1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          move(-1)
          break
        case 'Home':
          e.preventDefault()
          if (firstEnabled >= 0) select(firstEnabled)
          break
        case 'End': {
          e.preventDefault()
          for (let i = options.length - 1; i >= 0; i--) {
            if (!options[i].disabled) {
              select(i)
              break
            }
          }
          break
        }
      }
    }

    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        className={cn(
          'isolate inline-flex items-center gap-0.5 bg-fbui-gray-100',
          sizeStyles[size].root,
          fullWidth && 'flex w-full',
          className
        )}
      >
        {options.map((opt, index) => {
          const isSelected = opt.value === selected
          const isDisabled = disabled || opt.disabled
          return (
            <button
              key={opt.value}
              ref={(el) => {
                segmentRefs.current[index] = el
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={opt['aria-label']}
              disabled={isDisabled}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => select(index)}
              className={cn(
                'relative inline-flex items-center justify-center whitespace-nowrap font-medium outline-none transition-colors [&>svg]:shrink-0',
                'focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
                sizeStyles[size].segment,
                fullWidth && 'flex-1',
                isSelected
                  ? 'bg-fbui-gray-900 text-fbui-base-white'
                  : 'text-fbui-gray-600 enabled:hover:text-fbui-gray-900',
                isDisabled && 'cursor-not-allowed text-fbui-gray-300'
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          )
        })}
      </div>
    )
  }
)
SegmentedControl.displayName = 'SegmentedControl'
